import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Toast, type ToastType } from "@legal-500/legal500-design-system";
import { usePermissions } from "@/hooks/usePermissions";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { researchCms } from "@/api/researchCms.service";
import {
  AsyncSearchableSelect,
  type AsyncSelectOption,
} from "@/components/Form/AsyncSearchableSelect/AsyncSearchableSelect";
import { TaxonomyBpaTierSelector } from "@/components/ResearchCms/TaxonomyBpaTierSelector/TaxonomyBpaTierSelector";
import { CmsConfirmDialog } from "../../../components/ResearchCms/CmsConfirmDialog/CmsConfirmDialog";
import { DangerZone } from "../../../components/ResearchCms/DangerZone/DangerZone";
import { UnsavedChangesModal } from "../../../components/ResearchCms/UnsavedChangesModal/UnsavedChangesModal";
import { UnauthorizedView } from "../../../components/UnauthorizedView";
import { Button } from "@legal-500/legal500-design-system";
import { ROUTES } from "../../../routes";
import { CmsLayout } from "./components/CmsLayout";
import { ImportHistoryList } from "./components/BpaImport/ImportHistoryList";
import { archiveConfirmMessage } from "./components/BpaImport/importText";
import { archiveBlockedInSplitOrMergeReason } from "./utils/bpaArchive";
import {
  canImportContent,
  deleteRefusalMessage,
  type ImportDeleteRefusal,
  readImportDeleteRefusal,
} from "./utils/bpaImport";
import { importBlockedReason } from "./utils/bpaImportEditionStatus";
import {
  BpaAssignmentFields,
  BpaEditionLinkFields,
  RankingEntityPickerField,
  useBpaFormSearches,
} from "./components/BpaFormFields";
import { CreateRankingEntityModal } from "@/components/ResearchCms/CreateRankingEntityModal/CreateRankingEntityModal";
import { CreatePracticeGroupModal } from "@/components/ResearchCms/CreatePracticeGroupModal/CreatePracticeGroupModal";
import {
  SUBTYPES,
  SUBTYPE_LABELS,
  SUBMISSION_TYPES,
  REFEREE_TYPES,
  defaultSubmissionType,
} from "./components/BpaFormFields/rankingTableConstants";
import { useNavigationGuard } from "./hooks/useNavigationGuard";
import shared from "./components/cmsShared.module.css";
import styles from "./CmsCreateBpaPage.module.css";
import bpaPageStyles from "./CmsBpaPage.module.css";
import detailStyles from "./components/DetailsTab/DetailsTab.module.css";

const rankingTableSchema = z.object({
  existingId: z.string().optional(),
  name: z.string().min(1, "Required"),
  subtype: z.string().min(1, "Required"),
  submissionType: z.string(),
  refereeType: z.string(),
});

const editBpaSchema = z.object({
  name: z.string().min(1, "Name is required"),
  rankingEntityId: z.string().min(1, "Ranking entity is required"),
  practiceGroupId: z.string().nullable(),
  taxonomyBpaId: z.string().nullable(),
  sourceEditionBpaId: z.string().nullable(),
  previousEditionBpaId: z.string().nullable(),
  assignedResearcherId: z.string().nullable(),
  assignedEditorId: z.string().nullable(),
  rankingTables: z.array(rankingTableSchema),
});

type EditBpaFormValues = z.infer<typeof editBpaSchema>;

function refusalDetail(lines: string[]) {
  if (lines.length === 0) return undefined;
  return lines.map(line => <p key={line}>{line}</p>);
}

export function CmsEditBpaPage() {
  const { hasPermission } = usePermissions();
  const { hasFlag } = useFeatureFlags();
  const cmsExportEnabled = hasFlag("research-cms-1-enabled");
  const { id: publicationConfigId, bpaId } = useParams<{ id: string; bpaId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const editionConfigId = searchParams.get("edition") ?? "";

  const editionConfigQuery = useQuery({
    queryKey: ["research-edition-config", editionConfigId],
    queryFn: () => researchCms.getEdition(editionConfigId),
    enabled: Boolean(editionConfigId),
  });
  const editionConfig = editionConfigQuery.data?.editionConfig ?? null;
  const editionId = editionConfig?.editionId ?? "";
  const editionName = editionConfig?.editionName ?? "";
  const importBlocked = importBlockedReason(editionConfig?.status);
  const previousEditionConfigId = editionConfig?.previousEditionId ?? null;

  const previousConfigQuery = useQuery({
    queryKey: ["research-edition-config", previousEditionConfigId],
    queryFn: () => researchCms.getEdition(previousEditionConfigId!),
    enabled: Boolean(previousEditionConfigId),
  });
  const previousEditionId = previousConfigQuery.data?.editionConfig?.editionId ?? null;

  const editionNamesByEditionId = useMemo(() => {
    const map = new Map<string, string>();
    if (editionConfig)
      map.set(editionConfig.editionId, editionConfig.editionName ?? editionConfig.editionId);
    const prev = previousConfigQuery.data?.editionConfig;
    if (prev) map.set(prev.editionId, prev.editionName ?? prev.editionId);
    return map;
  }, [editionConfig, previousConfigQuery.data]);

  type BpaDetail = {
    id: string;
    name: string;
    rankingEntityId: string;
    rankingEntityName: string;
    practiceGroupId: string | null;
    practiceGroupName: string | null;
    status: string | null;
    assignedResearcherId: string | null;
    assignedEditorId: string | null;
    editionBpaId: string | null;
    taxonomyBpaId: string | null;
    previousEditionBpaId: string | null;
    sourceEditionBpaId: string | null;
    linkedEditionIds: string[];
  };

  const bpaQuery = useQuery({
    queryKey: ["research-bpa-detail", bpaId, editionId],
    queryFn: async () => {
      if (!bpaId) return null;
      const result = await researchCms.getBpaById(bpaId, editionId || undefined);
      return result.researchBpa as BpaDetail;
    },
    enabled: Boolean(bpaId) && Boolean(editionId),
  });
  const bpa = bpaQuery.data ?? null;

  const rankingTablesQuery = useQuery({
    queryKey: ["research-ranking-tables", bpaId, editionId],
    queryFn: () => researchCms.getRankingTables({ bpaId: bpaId!, editionId }),
    enabled: Boolean(bpaId) && Boolean(editionId),
  });
  const existingTables = rankingTablesQuery.data?.rankingTables ?? [];

  const linkedEditionNames = useMemo(() => {
    if (!bpa?.linkedEditionIds?.length) return [];
    return bpa.linkedEditionIds.map(id => editionNamesByEditionId.get(id) ?? id);
  }, [bpa?.linkedEditionIds, editionNamesByEditionId]);

  const researchersQuery = useQuery({
    queryKey: ["research-researchers"],
    queryFn: () => researchCms.getResearchers(),
  });
  const researchers = useMemo(
    () => (researchersQuery.data?.researchers ?? []).map(r => ({ value: r.id, label: r.name })),
    [researchersQuery.data],
  );

  const [activeTab, setActiveTab] = useState<
    "bpa" | "edition" | "metadata" | "content" | "history"
  >("bpa");
  const [isSaving, setIsSaving] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [importedInto, setImportedInto] = useState<ImportDeleteRefusal | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [pendingCrossEditionSave, setPendingCrossEditionSave] = useState<EditBpaFormValues | null>(
    null,
  );
  const [showNewEntity, setShowNewEntity] = useState(false);
  const [selectedEntityName, setSelectedEntityName] = useState("");
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [createdGroupOption, setCreatedGroupOption] = useState<AsyncSelectOption | null>(null);

  const editionBpaId = bpa?.editionBpaId ?? null;
  const canDelete = hasPermission("staff.research.delete");
  const canImport = canImportContent(cmsExportEnabled, hasPermission);

  const editionBpaQuery = useQuery({
    queryKey: ["research-edition-bpa", editionBpaId],
    queryFn: () => researchCms.getEditionBpa(editionBpaId!),
    enabled: !!editionBpaId,
  });
  const archiveBlockedReason = archiveBlockedInSplitOrMergeReason({
    canDelete,
    inSplitOrMerge: editionBpaQuery.data?.editionBpa.inSplitOrMerge,
  });

  const { data: importHistoryData } = useQuery({
    queryKey: ["import-history", editionBpaId],
    queryFn: () => researchCms.getImportHistory(editionBpaId!),
    enabled: !!editionBpaId && cmsExportEnabled,
  });

  const form = useForm<EditBpaFormValues>({
    resolver: zodResolver(editBpaSchema),
    defaultValues: {
      name: "",
      rankingEntityId: "",
      practiceGroupId: null,
      taxonomyBpaId: null,
      sourceEditionBpaId: null,
      previousEditionBpaId: null,
      assignedResearcherId: null,
      assignedEditorId: null,
      rankingTables: [],
    },
  });

  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    setError,
    formState: { errors, isDirty },
  } = form;

  const {
    fields: rtFields,
    append: rtAppend,
    remove: rtRemove,
  } = useFieldArray({
    control,
    name: "rankingTables",
  });

  const navGuard = useNavigationGuard(isDirty);

  useEffect(() => {
    if (!bpa) return;
    reset({
      name: bpa.name,
      rankingEntityId: bpa.rankingEntityId,
      practiceGroupId: bpa.practiceGroupId ?? null,
      taxonomyBpaId: bpa.taxonomyBpaId ?? null,
      sourceEditionBpaId: bpa.sourceEditionBpaId ?? null,
      previousEditionBpaId: bpa.previousEditionBpaId ?? null,
      assignedResearcherId: bpa.assignedResearcherId ?? null,
      assignedEditorId: bpa.assignedEditorId ?? null,
      rankingTables: existingTables.map(t => ({
        existingId: t.id,
        name: t.name,
        subtype: t.subtype,
        submissionType: t.submissionType ?? "",
        refereeType: t.refereeType ?? "",
      })),
    });
    setSelectedEntityName(bpa.rankingEntityName ? `${bpa.rankingEntityName}` : "");
  }, [bpa, existingTables, reset]);

  const rankingEntityId = form.watch("rankingEntityId");

  const { searchPracticeGroups, searchResearchers } = useBpaFormSearches({
    rankingEntityId,
    editionId,
    previousEditionId,
    researchers,
    editionNamesByEditionId,
  });

  function hasCrossEditionChanges(data: EditBpaFormValues): boolean {
    if (!bpa) return false;
    return (
      data.name !== bpa.name ||
      data.rankingEntityId !== bpa.rankingEntityId ||
      data.practiceGroupId !== (bpa.practiceGroupId ?? null) ||
      data.taxonomyBpaId !== (bpa.taxonomyBpaId ?? null)
    );
  }

  function describeCrossEditionChanges(data: EditBpaFormValues): string[] {
    if (!bpa) return [];
    const changes: string[] = [];
    if (data.name !== bpa.name) changes.push(`name from "${bpa.name}" to "${data.name}"`);
    if (data.rankingEntityId !== bpa.rankingEntityId) changes.push("ranking entity");
    if (data.practiceGroupId !== (bpa.practiceGroupId ?? null)) changes.push("practice group");
    if (data.taxonomyBpaId !== (bpa.taxonomyBpaId ?? null)) changes.push("taxonomy mapping");
    return changes;
  }

  function onSubmit(data: EditBpaFormValues) {
    if (!bpaId || !editionId) return;
    if (hasCrossEditionChanges(data)) {
      setPendingCrossEditionSave(data);
      return;
    }
    doSave(data);
  }

  function handleEntityCreated(entity: { id: string; name: string; type: string }) {
    form.setValue("rankingEntityId", entity.id, { shouldDirty: true });
    setSelectedEntityName(`${entity.name} (${entity.type})`);
    setShowNewEntity(false);
    queryClient.invalidateQueries({ queryKey: ["research-ranking-entities"] });
  }

  function handleGroupCreated(group: { id: string; name: string }) {
    form.setValue("practiceGroupId", group.id, { shouldDirty: true });
    setCreatedGroupOption({ value: group.id, label: group.name });
    setShowNewGroup(false);
  }

  async function doSave(data: EditBpaFormValues) {
    if (!bpaId || !editionId) return;
    setIsSaving(true);
    try {
      await Promise.all([
        researchCms.updateResearchBpa(bpaId, editionId, {
          name: data.name,
          rankingEntityId: data.rankingEntityId,
          practiceGroupId: data.practiceGroupId,
          taxonomyBpaId: data.taxonomyBpaId,
          sourceEditionBpaId: data.sourceEditionBpaId,
          previousEditionBpaId: data.previousEditionBpaId,
        }),
        ...(editionBpaId
          ? [
              researchCms.updateEditionBpa(editionBpaId, {
                assignedResearcherId: data.assignedResearcherId ?? null,
                assignedEditorId: data.assignedEditorId ?? null,
              }),
              researchCms.updateRankingTables(
                editionBpaId,
                data.rankingTables.map(rt => ({
                  id: rt.existingId,
                  name: rt.name,
                  subtype: rt.subtype,
                  submissionType: rt.submissionType || null,
                  refereeType: rt.refereeType || null,
                })),
              ),
            ]
          : []),
      ]);
      queryClient.invalidateQueries({ queryKey: ["research-edition-bpas"] });
      queryClient.invalidateQueries({ queryKey: ["research-bpa-stats"] });
      queryClient.invalidateQueries({ queryKey: ["research-bpa-detail", bpaId] });
      queryClient.invalidateQueries({ queryKey: ["research-ranking-tables", bpaId, editionId] });
      reset({ ...data, rankingTables: [] });
      setToast({ message: "Practice area updated", type: "success" });
    } catch (err) {
      setError("root.serverError", {
        type: "server",
        message: err instanceof Error ? err.message : "Failed to update",
      });
    } finally {
      setIsSaving(false);
    }
  }

  const isArchived = bpa?.status === "archived";

  async function handleArchive() {
    if (!editionBpaId) return;
    try {
      await researchCms.updateEditionBpa(editionBpaId, {
        status: "archived",
      } as Parameters<typeof researchCms.updateEditionBpa>[1]);
      setShowArchiveConfirm(false);
      queryClient.invalidateQueries({ queryKey: ["research-bpa-detail", bpaId, editionId] });
      queryClient.invalidateQueries({ queryKey: ["research-edition-bpas"] });
      setToast({ message: "Practice area archived.", type: "success" });
    } catch {
      setToast({ message: "Failed to archive practice area.", type: "error" });
      setShowArchiveConfirm(false);
    }
  }

  async function handleUnarchive() {
    if (!editionBpaId) return;
    try {
      await researchCms.updateEditionBpa(editionBpaId, { status: "not_started" });
      queryClient.invalidateQueries({ queryKey: ["research-bpa-detail", bpaId, editionId] });
      queryClient.invalidateQueries({ queryKey: ["research-edition-bpas"] });
      setToast({ message: "Practice area unarchived.", type: "success" });
    } catch {
      setToast({ message: "Failed to unarchive practice area.", type: "error" });
    }
  }

  async function handleRemoveFromEdition() {
    if (!editionBpaId) return;
    setIsRemoving(true);
    try {
      await researchCms.deleteEditionBpa(editionBpaId);
      queryClient.invalidateQueries({ queryKey: ["research-edition-bpas"] });
      queryClient.invalidateQueries({ queryKey: ["research-bpa-stats"] });
      reset();
      navigate(ROUTES.research.cmsManage(publicationConfigId ?? "", editionConfigId));
    } catch (error) {
      setShowRemoveConfirm(false);
      const importRefusal = readImportDeleteRefusal(error, editionId);
      if (importRefusal) {
        setImportedInto(importRefusal);
        return;
      }
      setToast({
        message:
          "Cannot remove this practice area from the edition. It still has ranking tables or editorial content — delete those first.",
        type: "error",
      });
    } finally {
      setIsRemoving(false);
    }
  }

  async function handleDeleteBpa() {
    if (!bpaId) return;
    setIsDeleting(true);
    try {
      await researchCms.deleteResearchBpa(bpaId);
      queryClient.invalidateQueries({ queryKey: ["research-edition-bpas"] });
      queryClient.invalidateQueries({ queryKey: ["research-bpa-stats"] });
      reset();
      navigate(ROUTES.research.cmsManage(publicationConfigId ?? "", editionConfigId));
    } catch {
      setToast({
        message:
          "Cannot delete this practice area. It still has edition links, taxonomy mappings, or WordPress mappings — remove those first.",
        type: "error",
      });
      setShowDeleteConfirm(false);
    } finally {
      setIsDeleting(false);
    }
  }

  if (!hasPermission("staff.research.edit")) return <UnauthorizedView />;

  return (
    <CmsLayout
      breadcrumbs={[
        {
          label: editionName || "Edition",
          path: editionId ? ROUTES.research.cmsPublication(editionId) : "",
        },
        {
          label: "Manage",
          path: ROUTES.research.cmsManage(publicationConfigId ?? "", editionConfigId),
        },
        {
          label: bpa?.name ?? "Practice area",
          path: editionId && bpaId ? ROUTES.research.cmsBpa(editionId, bpaId) : "",
        },
        { label: "Edit" },
      ]}
    >
      <div className={styles.page}>
        <h1 className={styles.pageTitle}>{bpa?.name ?? "Edit practice area"}</h1>
        {bpa && (
          <p className={styles.pageSubtitle}>
            {editionName || "This edition"} &middot; {bpa.rankingEntityName}
          </p>
        )}

        {bpaQuery.isLoading ? (
          <p>Loading...</p>
        ) : !bpa ? (
          <p>Practice area not found.</p>
        ) : (
          <>
            <div
              className={bpaPageStyles.tabBar}
              role="tablist"
              aria-label="Edit tabs"
              style={{ margin: "0 -24px", marginBottom: 16 }}
            >
              {(
                [
                  "bpa",
                  "edition",
                  "metadata",
                  ...(cmsExportEnabled ? (["content", "history"] as const) : []),
                ] as const
              ).map(tab => (
                <button
                  key={tab}
                  type="button"
                  className={`${bpaPageStyles.tab} ${activeTab === tab ? bpaPageStyles.tabActive : ""}`}
                  role="tab"
                  aria-selected={activeTab === tab}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab === "bpa"
                    ? "BPA details"
                    : tab === "edition"
                      ? "Edition"
                      : tab === "metadata"
                        ? "Metadata"
                        : tab === "content"
                          ? "Import & archive"
                          : "History"}
                </button>
              ))}
            </div>

            <form
              onSubmit={handleSubmit(onSubmit)}
              className={styles.form}
              style={{
                display: activeTab === "bpa" || activeTab === "edition" ? undefined : "none",
              }}
            >
              <div style={{ display: activeTab === "bpa" ? undefined : "none" }}>
                <section className={shared.sectionCard}>
                  <h2 className={shared.sectionHeading}>Practice area details</h2>
                  <div className={styles.warningBanner} role="status">
                    Changes to name, ranking entity, practice group, and taxonomy mapping affect
                    this BPA across
                    <strong> all editions</strong> it is linked to. If you only need changes for
                    this edition, create a new practice area instead.
                  </div>
                  <div className={styles.fieldGrid}>
                    <div className={styles.field}>
                      <label className={styles.label} htmlFor="bpa-name">
                        Practice area name{" "}
                        <span aria-hidden="true" style={{ color: "var(--color-danger, #ef4444)" }}>
                          *
                        </span>
                      </label>
                      <input id="bpa-name" className={styles.input} {...register("name")} />
                      {errors.name && <span className={styles.error}>{errors.name.message}</span>}
                    </div>

                    <div className={styles.field}>
                      <Controller
                        name="rankingEntityId"
                        control={control}
                        render={({ field }) => (
                          <RankingEntityPickerField
                            value={field.value || null}
                            onChange={val => field.onChange(val ?? "")}
                            onEntityChange={entity =>
                              setSelectedEntityName(entity ? `${entity.name} (${entity.type})` : "")
                            }
                            onCreateNew={() => setShowNewEntity(true)}
                            selectedLabel={selectedEntityName || null}
                            error={errors.rankingEntityId?.message}
                          />
                        )}
                      />
                    </div>

                    <div className={styles.field}>
                      <Controller
                        name="practiceGroupId"
                        control={control}
                        render={({ field }) => (
                          <AsyncSearchableSelect
                            name="practiceGroupId"
                            label="Practice group"
                            value={field.value}
                            onChange={val => field.onChange(val ?? null)}
                            onSearch={searchPracticeGroups}
                            placeholder="Search groups..."
                            minSearchLength={0}
                            initialOption={
                              createdGroupOption ??
                              (bpa.practiceGroupId && bpa.practiceGroupName
                                ? { value: bpa.practiceGroupId, label: bpa.practiceGroupName }
                                : null)
                            }
                          />
                        )}
                      />
                      {rankingEntityId && (
                        <button
                          type="button"
                          className={styles.inlineCreate}
                          onClick={() => setShowNewGroup(true)}
                        >
                          + Create new practice group
                        </button>
                      )}
                    </div>

                    <div className={styles.field}>
                      <label className={styles.label}>Taxonomy BPA</label>
                      <Controller
                        name="taxonomyBpaId"
                        control={control}
                        render={({ field }) => (
                          <TaxonomyBpaTierSelector
                            value={field.value}
                            onChange={val => field.onChange(val)}
                          />
                        )}
                      />
                    </div>
                  </div>
                </section>

                {errors.root?.serverError && (
                  <div className={styles.serverError} role="alert">
                    {errors.root.serverError.message}
                  </div>
                )}

                <div className={shared.footer}>
                  <button
                    type="button"
                    className={shared.discardButton}
                    onClick={() =>
                      navigate(
                        ROUTES.research.cmsManage(publicationConfigId ?? "", editionConfigId),
                      )
                    }
                  >
                    Cancel
                  </button>
                  <button type="submit" className={shared.saveButton} disabled={isSaving}>
                    {isSaving ? "Saving..." : "Save changes"}
                  </button>
                </div>
              </div>

              <div style={{ display: activeTab === "edition" ? undefined : "none" }}>
                <section className={shared.sectionCard}>
                  <h2 className={shared.sectionHeading}>Ranking tables</h2>
                  <p className={styles.sectionNote}>
                    Ranking tables are specific to this edition&rsquo;s practice area.
                  </p>

                  {rtFields.length === 0 && (
                    <p className={styles.sectionNote} style={{ fontStyle: "italic" }}>
                      No ranking tables configured for this edition.
                    </p>
                  )}

                  {rtFields.map((field, index) => {
                    const isExisting = Boolean(field.existingId);
                    return (
                      <div key={field.id} className={styles.tableCard}>
                        <div className={styles.tableCardHeader}>
                          <span className={styles.tableCardTitle}>
                            {isExisting ? field.name : `Table ${index + 1}`}
                          </span>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            {isExisting && (
                              <span className={styles.subtypePill}>
                                {SUBTYPE_LABELS[field.subtype] ?? field.subtype}
                              </span>
                            )}
                            <button
                              type="button"
                              className={styles.removeButton}
                              onClick={() => rtRemove(index)}
                              aria-label="Remove"
                            >
                              ×
                            </button>
                          </div>
                        </div>
                        <div className={styles.tableFields}>
                          <div className={styles.field}>
                            <label className={styles.label}>
                              Table name{" "}
                              <span
                                aria-hidden="true"
                                style={{ color: "var(--color-danger, #ef4444)" }}
                              >
                                *
                              </span>
                            </label>
                            <input
                              className={styles.input}
                              placeholder="e.g. Leading Individuals"
                              {...register(`rankingTables.${index}.name`)}
                            />
                            {errors.rankingTables?.[index]?.name && (
                              <span className={styles.error}>
                                {errors.rankingTables[index].name?.message}
                              </span>
                            )}
                          </div>
                          <div className={styles.field}>
                            <label className={styles.label}>
                              Subtype{" "}
                              <span
                                aria-hidden="true"
                                style={{ color: "var(--color-danger, #ef4444)" }}
                              >
                                *
                              </span>
                            </label>
                            <select
                              className={styles.select}
                              {...register(`rankingTables.${index}.subtype`, {
                                onChange: (e: React.ChangeEvent<HTMLSelectElement>) => {
                                  setValue(
                                    `rankingTables.${index}.submissionType`,
                                    defaultSubmissionType(e.target.value),
                                  );
                                },
                              })}
                            >
                              <option value="">Select...</option>
                              {SUBTYPES.map(st => (
                                <option key={st} value={st}>
                                  {SUBTYPE_LABELS[st]}
                                </option>
                              ))}
                            </select>
                            {errors.rankingTables?.[index]?.subtype && (
                              <span className={styles.error}>
                                {errors.rankingTables[index].subtype?.message}
                              </span>
                            )}
                          </div>
                          <div className={styles.field}>
                            <label className={styles.label}>Submission type</label>
                            <select
                              className={styles.select}
                              {...register(`rankingTables.${index}.submissionType`)}
                            >
                              {SUBMISSION_TYPES.map(t => (
                                <option key={t.value} value={t.value}>
                                  {t.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className={styles.field}>
                            <label className={styles.label}>Referee type</label>
                            <select
                              className={styles.select}
                              {...register(`rankingTables.${index}.refereeType`)}
                            >
                              {REFEREE_TYPES.map(t => (
                                <option key={t.value} value={t.value}>
                                  {t.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  <button
                    type="button"
                    className={styles.addTableButton}
                    onClick={() =>
                      rtAppend({ name: "", subtype: "", submissionType: "", refereeType: "" })
                    }
                  >
                    + Add ranking table
                  </button>
                </section>

                <BpaAssignmentFields
                  control={control}
                  searchResearchers={searchResearchers}
                  editionName={editionName || "current"}
                  initialResearcherOption={
                    bpa.assignedResearcherId
                      ? (researchers.find(r => r.value === bpa.assignedResearcherId) ?? null)
                      : null
                  }
                  initialEditorOption={
                    bpa.assignedEditorId
                      ? (researchers.find(r => r.value === bpa.assignedEditorId) ?? null)
                      : null
                  }
                />

                <BpaEditionLinkFields
                  control={control}
                  previousEditionId={previousEditionId}
                  editionId={editionId}
                  editionNamesByEditionId={editionNamesByEditionId}
                />

                {editionBpaId && (
                  <DangerZone
                    heading="Danger zone"
                    actions={[
                      ...(isArchived
                        ? [
                            {
                              label: "unarchive",
                              description: (
                                <>
                                  Restores <strong>{bpa.name}</strong> to the active list with a
                                  status of Not Started.
                                </>
                              ),
                              buttonLabel: "Unarchive",
                              onAction: handleUnarchive,
                            },
                          ]
                        : [
                            {
                              label: "archive",
                              description: (
                                <>
                                  Hide <strong>{bpa.name}</strong> from the edition list. All
                                  ranking tables, editorial content, and assignments are kept — you
                                  can unarchive it later to pick up where you left off.
                                </>
                              ),
                              buttonLabel: "Archive",
                              onAction: () => setShowArchiveConfirm(true),
                              disabledReason: archiveBlockedReason,
                            },
                          ]),
                      ...(canDelete
                        ? [
                            {
                              label: "remove",
                              description: (
                                <>
                                  Detach <strong>{bpa.name}</strong> from{" "}
                                  <strong>{editionName || "this edition"}</strong> and delete all of
                                  its edition-specific data (ranking tables, editorial content,
                                  assignments). The practice area itself is kept and can be re-added
                                  to an edition later.
                                </>
                              ),
                              buttonLabel: "Remove from edition",
                              onAction: () => setShowRemoveConfirm(true),
                            },
                            {
                              label: "delete",
                              description: (
                                <>
                                  Permanently delete <strong>{bpa.name}</strong> across all
                                  editions. This cannot be undone. All edition links, taxonomy
                                  mappings, and WordPress mappings must be removed first.
                                </>
                              ),
                              buttonLabel: "Delete practice area",
                              onAction: () => setShowDeleteConfirm(true),
                            },
                          ]
                        : []),
                    ]}
                  />
                )}

                {errors.root?.serverError && (
                  <div className={styles.serverError} role="alert">
                    {errors.root.serverError.message}
                  </div>
                )}

                <div className={shared.footer}>
                  <button
                    type="button"
                    className={shared.discardButton}
                    onClick={() =>
                      navigate(
                        ROUTES.research.cmsManage(publicationConfigId ?? "", editionConfigId),
                      )
                    }
                  >
                    Cancel
                  </button>
                  <button type="submit" className={shared.saveButton} disabled={isSaving}>
                    {isSaving ? "Saving..." : "Save changes"}
                  </button>
                </div>
              </div>
            </form>

            {activeTab === "metadata" &&
              (() => {
                const metadataFields: [string, string][] = [
                  ["BPA ID", bpa.id],
                  ["Edition BPA ID", editionBpaId ?? "—"],
                  ["Ranking Entity ID", bpa.rankingEntityId],
                  ["Ranking Entity Name", bpa.rankingEntityName ?? "—"],
                  ["Practice Group ID", bpa.practiceGroupId ?? "—"],
                  ["Taxonomy BPA ID", bpa.taxonomyBpaId ?? "—"],
                  ["Edition ID", editionId],
                  ["Edition Config ID", editionConfigId],
                  ["Edition Name", editionName || "—"],
                  ["Status", bpa.status ?? "—"],
                  ...bpa.linkedEditionIds.map(
                    (eid, i) =>
                      [
                        `Linked Edition ${i + 1}`,
                        `${editionNamesByEditionId.get(eid) ?? "Unknown"} (${eid})`,
                      ] as [string, string],
                  ),
                ];
                return (
                  <div className={shared.sectionCard}>
                    <div className={detailStyles.detailsHeader}>
                      <h2 className={`${shared.sectionHeading} ${shared.headingInline}`}>
                        Metadata
                      </h2>
                      <button
                        type="button"
                        className={detailStyles.copyAllButton}
                        onClick={() => {
                          const lines = metadataFields.map(([l, v]) => `${l}: ${v}`).join("\n");
                          navigator.clipboard.writeText(lines);
                        }}
                      >
                        Copy All
                      </button>
                    </div>
                    <div className={detailStyles.detailsGrid}>
                      {metadataFields.map(([label, value]) => (
                        <div key={label} className={detailStyles.detailRow}>
                          <span className={detailStyles.detailLabel}>{label}</span>
                          <span className={detailStyles.detailValue}>{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

            {activeTab === "content" && (
              <div className={shared.sectionCard}>
                <h2 className={shared.sectionHeading}>Import content</h2>
                <p
                  style={{ fontSize: 13, color: "var(--color-text-secondary)", margin: "0 0 16px" }}
                >
                  Import submissions, rankings, and commentary from another practice area into this
                  one.
                </p>
                {canImport && (
                  <Button
                    variant="outlined"
                    onClick={() => navigate(ROUTES.research.cmsBpaImport(editionId, bpaId!))}
                    disabled={!editionId || !bpaId || isArchived || importBlocked !== null}
                    aria-describedby={importBlocked ? "import-blocked-reason" : undefined}
                  >
                    Import content
                  </Button>
                )}
                {canImport && importBlocked && (
                  <span id="import-blocked-reason" className={bpaPageStyles.importBlockedReason}>
                    {importBlocked}
                  </span>
                )}

                <div style={{ marginTop: 32 }}>
                  <DangerZone
                    heading="Archive practice area"
                    buttonLabel="Archive"
                    onAction={() => setShowArchiveConfirm(true)}
                    disabled={!editionBpaId || isArchived}
                    disabledReason={archiveBlockedReason}
                  >
                    Archiving will hide this practice area from the CMS and make its content
                    non-publishable. You can unarchive it later.
                  </DangerZone>
                </div>
              </div>
            )}

            {activeTab === "history" && (
              <div className={shared.sectionCard}>
                <h2 className={shared.sectionHeading}>Import history</h2>
                <ImportHistoryList
                  editionId={editionId}
                  editionBpaId={editionBpaId}
                  entries={importHistoryData?.importHistory ?? []}
                  firmNamesUnavailable={importHistoryData?.firmNamesUnavailable ?? false}
                />
              </div>
            )}
          </>
        )}
      </div>

      {navGuard.blocked && (
        <UnsavedChangesModal
          onCancel={navGuard.cancel}
          onDiscard={() => {
            reset();
            navGuard.proceed();
          }}
        />
      )}

      {pendingCrossEditionSave && bpa && (
        <CmsConfirmDialog
          title="Changes affect all editions"
          message={`You are changing the ${describeCrossEditionChanges(pendingCrossEditionSave).join(", ")} of this practice area. These changes will apply to every edition it is linked to.`}
          affected={
            linkedEditionNames.length > 0
              ? [
                  {
                    heading: `Affected editions (${linkedEditionNames.length})`,
                    items: linkedEditionNames.map(name => ({ id: name, name })),
                    remaining: 0,
                  },
                ]
              : undefined
          }
          tone="warning"
          confirmLabel={isSaving ? "Saving..." : "Save changes"}
          onCancel={() => !isSaving && setPendingCrossEditionSave(null)}
          onConfirm={() => {
            doSave(pendingCrossEditionSave);
            setPendingCrossEditionSave(null);
          }}
        />
      )}

      {showArchiveConfirm && bpa && (
        <CmsConfirmDialog
          title="Archive practice area"
          message={archiveConfirmMessage(
            bpa.name,
            editionBpaQuery.data?.editionBpa.mergeSourceCount ?? 0,
          )}
          tone="warning"
          confirmLabel="Archive"
          cancelLabel="Cancel"
          onCancel={() => setShowArchiveConfirm(false)}
          onConfirm={handleArchive}
        />
      )}

      {showRemoveConfirm && bpa && (
        <CmsConfirmDialog
          title="Remove from edition"
          message={`Remove ${bpa.name} from ${editionName || "this edition"}? This will delete all ranking tables, editorial content, and assignments for this edition.`}
          tone="danger"
          confirmLabel={isRemoving ? "Removing..." : "Remove"}
          onCancel={() => !isRemoving && setShowRemoveConfirm(false)}
          onConfirm={handleRemoveFromEdition}
        />
      )}

      {importedInto && (
        <CmsConfirmDialog
          title={deleteRefusalMessage(importedInto.names, importedInto.copiedOnly).title}
          message={deleteRefusalMessage(importedInto.names, importedInto.copiedOnly).body[0]}
          detail={refusalDetail(
            deleteRefusalMessage(importedInto.names, importedInto.copiedOnly).body.slice(1),
          )}
          tone="warning"
          confirmLabel="OK"
          onCancel={() => setImportedInto(null)}
          onConfirm={() => setImportedInto(null)}
        />
      )}

      {showDeleteConfirm && bpa && (
        <CmsConfirmDialog
          title="Delete practice area"
          message={`Permanently delete "${bpa.name}"? This cannot be undone.`}
          tone="danger"
          confirmLabel={isDeleting ? "Deleting..." : "Delete"}
          onCancel={() => !isDeleting && setShowDeleteConfirm(false)}
          onConfirm={handleDeleteBpa}
        />
      )}

      <CreateRankingEntityModal
        open={showNewEntity}
        onClose={() => setShowNewEntity(false)}
        onCreated={handleEntityCreated}
      />

      <CreatePracticeGroupModal
        open={showNewGroup}
        rankingEntityId={rankingEntityId || bpa?.rankingEntityId || ""}
        rankingEntityName={selectedEntityName || bpa?.rankingEntityName || ""}
        onClose={() => setShowNewGroup(false)}
        onCreated={handleGroupCreated}
      />

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </CmsLayout>
  );
}
